import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, Shield, Edit, Eye, Users, ArrowRight, LogOut } from 'lucide-react';
import LanguageSwitcher from '../../components/Navigation/LanguageSwitcher';

const TenantSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectTenant, logout, ensureTenantContext } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedTenantCode, setSelectedTenantCode] = useState('');
  const [error, setError] = useState('');

  // Get tenants from location state or user context
  const tenants = location.state?.tenants || user?.tenants || [];


  useEffect(() => {
    // If user is not logged in or is admin, redirect appropriately
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.userType === 'admin') {
      navigate('/admin');
      return;
    }

    // If user has no tenants or only one tenant, redirect to dashboard
    if (tenants.length === 0) {
      navigate('/dashboard');
      return;
    }

    if (tenants.length === 1 && !user.requiresTenantSelection) {
      // Auto-select single tenant and redirect
      handleTenantSelection(tenants[0].tenant_code || tenants[0].code);
      return;
    }

    // If user already has a selected tenant and doesn't require selection, go to dashboard
    if (user.selectedTenant && !user.requiresTenantSelection) {
      navigate('/dashboard');
      return;
    }
  }, [user, tenants, navigate]);

  const handleTenantSelection = async (tenantCode) => {
    if (!tenantCode) {
      setError('Please select a tenant to continue');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔍 TENANT_SELECTION: Calling selectTenant with:', tenantCode);
      const result = await selectTenant(tenantCode);
      console.log('🔍 TENANT_SELECTION: selectTenant result:', result);
      
      if (result.success) {
        // Ensure tenant context is properly set
        await ensureTenantContext();
        console.log('🔍 TENANT_SELECTION: Tenant context ensured, navigating to dashboard');
        navigate('/dashboard');
      } else {
        console.error('🔍 TENANT_SELECTION: Failed to select tenant:', result.error);
        setError(result.error || 'Failed to select tenant');
      }
    } catch (err) {
      console.error('🔍 TENANT_SELECTION: Exception during tenant selection:', err);
      setError('Failed to select tenant. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getAccessLevelIcon = (accessLevel) => {
    switch (accessLevel) {
      case 'Admin':
        return <Shield className="h-5 w-5 text-red-400" />;
      case 'Write':
        return <Edit className="h-5 w-5 text-yellow-400" />;
      case 'Read':
      default:
        return <Eye className="h-5 w-5 text-blue-400" />;
    }
  };

  const getAccessLevelColor = (accessLevel) => {
    switch (accessLevel) {
      case 'Admin':
        return 'border-red-500 bg-red-500/10';
      case 'Write':
        return 'border-yellow-500 bg-yellow-500/10';
      case 'Read':
      default:
        return 'border-blue-500 bg-blue-500/10';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="backdrop-blur-md bg-gray-900/95 p-8 rounded-xl shadow-2xl border border-gray-700/50">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <span className="text-white text-lg">Setting up your workspace...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-4">
      <div className="backdrop-blur-md bg-gray-900/95 p-8 rounded-xl shadow-2xl w-full max-w-2xl border border-gray-700/50">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <Building2 className="h-8 w-8 text-blue-400" />
              <h2 className="text-3xl font-extrabold text-white">Select Your Workspace</h2>
            </div>
            <p className="text-gray-300">
              {user?.firstName ? `Welcome ${user.firstName}!` : 'Welcome!'} You have access to multiple workspaces. Please select one to continue.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <img src="/cmr_logo.png" alt="CRM Logo" className="h-12 w-auto" />
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}


        <div className="space-y-6">
          {/* Tenant List */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-300 mb-4">
              Available Workspaces ({tenants.length}) - Click to select
            </label>
            
            <div className="grid gap-4 max-h-96 overflow-y-auto">
              {tenants.map((tenant) => {
                const tenantCode = tenant.tenant_code || tenant.code;
                const tenantName = tenant.tenant_name || tenant.name;
                const accessLevel = tenant.access_level || tenant.accessLevel || 'Read';
                const isDefault = tenant.is_default || tenant.isDefault;

                return (
                  <label
                    key={tenantCode}
                    className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:scale-102 ${
                      loading && selectedTenantCode === tenantCode
                        ? `${getAccessLevelColor(accessLevel)} shadow-lg scale-105 opacity-75`
                        : selectedTenantCode === tenantCode
                        ? `${getAccessLevelColor(accessLevel)} shadow-lg scale-105`
                        : 'border-gray-600 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-800/70'
                    } ${loading && selectedTenantCode !== tenantCode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="tenant"
                      value={tenantCode}
                      checked={selectedTenantCode === tenantCode}
                      disabled={loading}
                      onChange={(e) => {
                        if (!loading) {
                          setSelectedTenantCode(e.target.value);
                          handleTenantSelection(e.target.value);
                        }
                      }}
                      className="sr-only"
                    />
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <Building2 className="h-6 w-6 text-gray-400" />
                          <div>
                            <h3 className="text-lg font-semibold text-white">
                              {tenantName}
                            </h3>
                            <p className="text-sm text-gray-400">Code: {tenantCode}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          {isDefault && (
                            <span className="px-2 py-1 bg-green-500/20 border border-green-500/50 rounded text-xs text-green-300">
                              Default
                            </span>
                          )}
                          <div className="flex items-center space-x-2">
                            {getAccessLevelIcon(accessLevel)}
                            <span className="text-sm text-gray-300">{accessLevel}</span>
                          </div>
                        </div>
                      </div>
                      
                      {tenant.description && (
                        <p className="text-sm text-gray-400 mt-2">
                          {tenant.description}
                        </p>
                      )}
                    </div>
                    
                    {selectedTenantCode === tenantCode && (
                      loading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400 ml-3"></div>
                      ) : (
                        <ArrowRight className="h-6 w-6 text-blue-400 ml-3" />
                      )
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center justify-center py-3 px-4 border border-gray-600 rounded-lg text-sm font-medium text-gray-300 bg-gray-800/50 hover:bg-gray-700/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 focus:ring-offset-gray-800 transition-all duration-200"
            >
              <LogOut className="h-5 w-5 mr-2" />
              Logout
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <Users className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="text-sm text-blue-200">
              <p className="font-medium mb-1">Access Levels:</p>
              <ul className="space-y-1 text-blue-300">
                <li><span className="text-red-400">Admin:</span> Full administrative access</li>
                <li><span className="text-yellow-400">Write:</span> Can view and modify data</li>
                <li><span className="text-blue-400">Read:</span> View-only access</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSelection;
